import { captureEventContext } from "@app/config/loggers/analyticsContext";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useCallback, useEffect, useRef } from "react";
import type { ZodError } from "zod";
import { DAILY_LOG_STEPS, type DailyLogStep } from "./constants";
import type { DailyLogFormValues } from "./schema";

/**
 * Zod 오류에서 **최상위 필드명만** 중복 없이 뽑는다.
 *
 * `issue.path` 를 그대로 이어 붙이면 `workforce_contractors.0.workforces.1.quantity` 처럼
 * 배열 인덱스가 섞여 값마다 항목이 갈라진다. 알고 싶은 건 "몇 번째 행"이 아니라
 * "어느 입력이 사람을 막는가"이므로 첫 세그먼트로 줄여 집계 가능하게 만든다.
 */
export function topLevelIssueFields(error: ZodError): string[] {
  const fields = new Set<string>();
  for (const issue of error.issues) {
    const [first] = issue.path;
    fields.add(first === undefined ? "(root)" : String(first));
  }
  return Array.from(fields);
}

/** 신규 작성(create)과 기존 로그 수정(edit)은 완료율 기준이 다르므로 분리해서 본다. */
export type DailyLogWriteMode = "create" | "edit";

/** 제출이 실패한 이유. validation 은 서버에 닿기도 전에 막힌 경우다. */
export type DailyLogSubmitFailReason = "validation" | "api";

/**
 * 작성 화면을 떠난 이유.
 * - `user_exit`: 사용자가 스스로 화면을 벗어났다. 진짜 이탈은 이것뿐이다.
 * - `redirected`: 앱이 강제로 내보냈다(lock 보유자 충돌, 로그 없음 등). 사용자의 포기가 아니다.
 * - `session_end`: 탭·앱이 닫혔다. 마지막 단계까지만 알 수 있다.
 */
export type DailyLogAbandonReason = "user_exit" | "redirected" | "session_end";

interface UseDailyLogWriteTrackerOptions {
  projectId: string;
  mode: DailyLogWriteMode;
  /** create 모드에서 사용 중인 드래프트 식별자 */
  draftId?: string;
}

interface SubmitFailProperties {
  reason: DailyLogSubmitFailReason;
  /** API 실패 시 서버가 내려준 에러 코드 */
  errorCode?: string;
  /**
   * validation 으로 막힌 필드 이름.
   *
   * 중첩 경로(`workforce_contractors.0.workforces.1.quantity`)를 그대로 넣으면 인덱스마다
   * 값이 갈라져 집계가 불가능하므로 **최상위 필드명만** 넘긴다.
   */
  failedFields?: string[];
}

export interface DailyLogWriteTracker {
  /** 퍼널 단계 진입. 단계별 이탈 지점을 계산하는 기준값이 된다. */
  trackStepView: (step: DailyLogStep) => void;
  /** Save 버튼을 눌러 제출 시도가 시작된 시점 */
  trackSubmitStart: () => void;
  trackSubmitSuccess: (values: DailyLogFormValues) => void;
  trackSubmitFail: (properties: SubmitFailProperties) => void;
  /**
   * 앱이 사용자를 강제로 내보내기 직전에 호출한다.
   * 이후 발생하는 이탈 이벤트가 `reason: "redirected"` 로 기록돼
   * 진짜 이탈 분포에서 걸러낼 수 있다.
   */
  markRedirected: () => void;
}

const INITIAL_STEP: DailyLogStep = "Overview";

/**
 * 입력 행 수가 아니라 실제 인원·장비 수량의 합.
 * 한 직종에 10명을 넣어도 행은 1개라, 행 개수만 보면 현장 규모를 알 수 없다.
 */
function sumQuantity(items: { quantity: number }[]): number {
  return items.reduce((total, item) => total + (Number.isFinite(item.quantity) ? item.quantity : 0), 0);
}

function countWorkforceEntries(values: DailyLogFormValues): number {
  return values.workforce_contractors.reduce((total, contractor) => total + contractor.workforces.length, 0);
}

function countEquipmentEntries(values: DailyLogFormValues): number {
  return values.equipment_contractors.reduce((total, contractor) => total + contractor.equipments.length, 0);
}

function sumWorkforceQuantity(values: DailyLogFormValues): number {
  return values.workforce_contractors.reduce((total, contractor) => total + sumQuantity(contractor.workforces), 0);
}

function sumEquipmentQuantity(values: DailyLogFormValues): number {
  return values.equipment_contractors.reduce((total, contractor) => total + sumQuantity(contractor.equipments), 0);
}

/**
 * 일일 로그 작성 퍼널의 진입 → 단계 이동 → 제출/이탈을 추적한다.
 *
 * 지금까지는 자동 페이지뷰로 `/log/new` 도달까지만 보였고, 제출 성공과 중도 이탈이
 * 똑같이 "라우트 이탈"로 관측돼 작성 완료율을 계산할 수 없었다.
 * 이 훅은 그 구간을 메워 완료율·소요 시간·이탈 단계를 산출 가능하게 한다.
 *
 * 마운트 시 `daily_log_write_start`, 제출이 확정되지 않은 채 화면을 떠나면
 * `daily_log_write_abandon` 을 정확히 한 번 보낸다. 두 이벤트의 차이가 곧 이탈이다.
 *
 * 이탈은 세 경로로 관측한다 — 언마운트(앱 내 이동), `pagehide`(탭·앱 종료),
 * 그리고 `markRedirected()` 로 표시된 강제 리다이렉트. 셋 다 `reason` 으로 구분되므로
 * 진짜 이탈만 골라 볼 수 있고, `write_start` 대비 종료 이벤트 수도 어긋나지 않는다.
 *
 * 남는 사각지대: 앱을 백그라운드로 보낸 뒤 OS 가 나중에 프로세스를 죽이면
 * `pagehide` 조차 발생하지 않는다. 이 경우는 여전히 집계되지 않는다.
 * (`visibilitychange` 에서 보내면 잡히지만, 되돌아와 제출한 세션까지
 *  이탈로 기록돼 성공과 이탈이 동시에 서는 문제가 생겨 택하지 않았다.)
 */
export function useDailyLogWriteTracker({
  projectId,
  mode,
  draftId,
}: UseDailyLogWriteTrackerOptions): DailyLogWriteTracker {
  const startedAtRef = useRef(Date.now());
  /**
   * 작성 화면에 들어온 시점의 화면·현장 컨텍스트.
   *
   * 이탈 이벤트는 언마운트 시점에 전송되는데, 그때는 이미 라우터가 다음 화면으로
   * 넘어간 뒤다. 전송 시점의 화면을 그대로 쓰면 "작성 화면에서 이탈"이
   * "이동한 목적지에서 이탈"로 기록돼, 강제 리다이렉트 경로에서는 100% 어긋난다.
   */
  const eventContextRef = useRef(captureEventContext());
  const lastStepRef = useRef<DailyLogStep>(INITIAL_STEP);
  /** 현재 단계에 들어온 시각. 단계별 체류 시간의 기준점. */
  const stepEnteredAtRef = useRef(Date.now());
  /**
   * 단계별 진입 횟수. 2 이상이면 사용자가 되돌아왔다는 뜻이고,
   * 되돌아옴이 잦은 단계가 곧 입력이 어려운 단계다.
   */
  const stepVisitCountsRef = useRef(new Map<DailyLogStep, number>());
  /** 제출이 성공해 정상 종료된 세션. 이 경우 abandon 을 보내지 않는다. */
  const isSettledRef = useRef(false);
  /** abandon 은 어느 경로로 관측되든 세션당 1회. */
  const isAbandonReportedRef = useRef(false);
  /** 앱이 강제로 내보낸 경우 true. 사용자의 포기와 구분하기 위한 표시. */
  const isRedirectedRef = useRef(false);

  const contextRef = useRef({ projectId, mode, draftId });
  contextRef.current = { projectId, mode, draftId };

  const reportAbandon = useCallback((reason: DailyLogAbandonReason) => {
    if (isSettledRef.current || isAbandonReportedRef.current) return;
    isAbandonReportedRef.current = true;

    const context = contextRef.current;

    EventLogger.track(
      CLIENT_EVENT.DAILY_LOG_WRITE_ABANDON,
      {
        project_id: context.projectId,
        mode: context.mode,
        draft_id: context.draftId ?? null,
        reason: isRedirectedRef.current ? "redirected" : reason,
        last_step: lastStepRef.current,
        last_step_index: DAILY_LOG_STEPS.indexOf(lastStepRef.current),
        duration_ms: Date.now() - startedAtRef.current,
        last_step_duration_ms: Date.now() - stepEnteredAtRef.current,
      },
      {
        context: eventContextRef.current,
        // 탭이 사라지는 중이면 일반 XHR 은 취소된다.
        ...(reason === "session_end" ? { transport: "sendBeacon" as const } : {}),
      },
    );
  }, []);

  useEffect(() => {
    const context = contextRef.current;
    startedAtRef.current = Date.now();
    eventContextRef.current = captureEventContext();
    stepEnteredAtRef.current = Date.now();
    lastStepRef.current = INITIAL_STEP;
    stepVisitCountsRef.current = new Map([[INITIAL_STEP, 1]]);
    isSettledRef.current = false;
    isAbandonReportedRef.current = false;
    isRedirectedRef.current = false;

    EventLogger.track(CLIENT_EVENT.DAILY_LOG_WRITE_START, {
      project_id: context.projectId,
      mode: context.mode,
      draft_id: context.draftId ?? null,
    });

    EventLogger.view(CLIENT_EVENT.DAILY_LOG_STEP_VIEW, {
      project_id: context.projectId,
      mode: context.mode,
      step: INITIAL_STEP,
      step_index: 0,
      // 첫 단계에는 "직전 단계"가 없다. 0 으로 보내면 즉시 통과한 것처럼 보이므로 구분한다.
      time_since_previous_step_ms: null,
      step_visit_count: 1,
    });

    // bfcache 로 들어가는 pagehide(persisted === true)는 되돌아올 수 있으므로 이탈이 아니다.
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      reportAbandon("session_end");
    };
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      reportAbandon("user_exit");
    };
  }, [reportAbandon]);

  const trackStepView = useCallback((step: DailyLogStep) => {
    const now = Date.now();
    const timeOnPreviousStep = now - stepEnteredAtRef.current;

    lastStepRef.current = step;
    stepEnteredAtRef.current = now;

    const visitCount = (stepVisitCountsRef.current.get(step) ?? 0) + 1;
    stepVisitCountsRef.current.set(step, visitCount);

    EventLogger.view(CLIENT_EVENT.DAILY_LOG_STEP_VIEW, {
      project_id: contextRef.current.projectId,
      mode: contextRef.current.mode,
      step,
      step_index: DAILY_LOG_STEPS.indexOf(step),
      time_since_previous_step_ms: timeOnPreviousStep,
      step_visit_count: visitCount,
    });
  }, []);

  const trackSubmitStart = useCallback(() => {
    EventLogger.click(CLIENT_EVENT.DAILY_LOG_SUBMIT_START, {
      project_id: contextRef.current.projectId,
      mode: contextRef.current.mode,
      duration_ms: Date.now() - startedAtRef.current,
    });
  }, []);

  const trackSubmitSuccess = useCallback((values: DailyLogFormValues) => {
    isSettledRef.current = true;

    EventLogger.result(CLIENT_EVENT.DAILY_LOG_SUBMIT_SUCCESS, {
      project_id: contextRef.current.projectId,
      mode: contextRef.current.mode,
      draft_id: contextRef.current.draftId ?? null,
      duration_ms: Date.now() - startedAtRef.current,
      image_count: values.image_urls.length,
      // 입력 행 수(`*_entry_count`)와 실제 수량 합(`*_count`)은 다르다.
      // 한 직종에 10명을 넣으면 행은 1, 인원은 10이다.
      workforce_entry_count: countWorkforceEntries(values),
      workforce_count: sumWorkforceQuantity(values),
      equipment_entry_count: countEquipmentEntries(values),
      equipment_count: sumEquipmentQuantity(values),
      tag_count: values.tags.length,
      description_length: values.description.length,
    });
  }, []);

  const trackSubmitFail = useCallback(({ reason, errorCode, failedFields }: SubmitFailProperties) => {
    EventLogger.result(CLIENT_EVENT.DAILY_LOG_SUBMIT_FAIL, {
      project_id: contextRef.current.projectId,
      mode: contextRef.current.mode,
      draft_id: contextRef.current.draftId ?? null,
      duration_ms: Date.now() - startedAtRef.current,
      reason,
      error_code: errorCode ?? null,
      last_step: lastStepRef.current,
      failed_fields: failedFields ?? [],
      failed_field_count: failedFields?.length ?? 0,
    });
  }, []);

  const markRedirected = useCallback(() => {
    isRedirectedRef.current = true;
  }, []);

  return { trackStepView, trackSubmitStart, trackSubmitSuccess, trackSubmitFail, markRedirected };
}
